import React from 'react';
import { useDispatch } from 'react-redux';

import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Table from '../../components/Table';
import SelectBox from '../../components/SelectBox';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';
import NetworkError from '../../components/Error';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import { getObservedSurfaceInitials, getObservedSurface } from '../../redux/actions/watcherActions';

import styles from './AttackSurface.module.css';

// External Baseline Comms: a baseline of the external systems each internal
// device is observed communicating with — which internal source reaches which
// external host, over which ports, how often. A visibility baseline, not an
// action surface.

const WINDOWS = [
	['7', 'Last 7 Days'],
	['30', 'Last 30 Days'],
	['90', 'Last 90 Days'],
];

const buildSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({ errorCode, pageInfo, sourceMessage });

const AttackSurfaceScreen = () => {
	const dispatch = useDispatch();

	const [selectedWatcher, setSelectedWatcher] = React.useState('');
	const [days, setDays] = React.useState('30');
	const [searchKey, setSearchKey] = React.useState('');

	const [dataLoading, setDataLoading] = React.useState(false);
	const [watchers, setWatchers] = React.useState([]);
	const [columns, setColumns] = React.useState([]);
	const [rows, setRows] = React.useState([]);
	const [summary, setSummary] = React.useState(null);
	const [hasError, setHasError] = React.useState(false);
	const [supportErrorMessage, setSupportErrorMessage] = React.useState('');
	const [copiedSupportError, setCopiedSupportError] = React.useState(false);

	const setSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearSupportError = React.useCallback((force = false) => {
		if (!force) return;
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(() => {
		if (!supportErrorMessage) return;
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(supportErrorMessage).then(
				() => setCopiedSupportError(true),
				() => setCopiedSupportError(false)
			);
		}
	}, [supportErrorMessage]);

	// Initial load: the watcher list for the selector.
	React.useEffect(() => {
		let active = true;
		(async () => {
			const data = await dispatch(getObservedSurfaceInitials());
			if (!active) return;
			if (data && data.status && Array.isArray(data.watchers)) {
				setWatchers(data.watchers);
			} else if (data && !data.status) {
				setHasError(true);
				setSupportError(
					ErrorCodes.API_INTERNAL_ERROR,
					'Attack Surface Page / Initial Data Load Failed',
					data?.msg || 'Observed surface init did not return success'
				);
			}
		})();
		return () => {
			active = false;
		};
	}, [dispatch, setSupportError]);

	// Fetch the surface whenever the watcher or window changes.
	React.useEffect(() => {
		let active = true;
		(async () => {
			if (!selectedWatcher) return;   // nothing selected yet — render with no data
			setDataLoading(true);
			const formData = new FormData();
			formData.append('watcherId', selectedWatcher);
			formData.append('days', days);
			try {
				const data = await dispatch(getObservedSurface(formData));
				if (!active) return;
				if (data && data.status) {
					setColumns(Array.isArray(data.SurfaceCols) ? data.SurfaceCols : []);
					setRows(Array.isArray(data.SurfaceRows) ? data.SurfaceRows : []);
					setSummary(data.summary || null);
					clearSupportError(true);
					setHasError(false);
				} else {
					setHasError(true);
					setSupportError(
						ErrorCodes.API_INTERNAL_ERROR,
						'Attack Surface Page / Surface Data Load Failed',
						data?.msg || (typeof data === 'string' ? data : 'Observed surface request did not return success')
					);
				}
			} catch (error) {
				if (!active) return;
				setHasError(true);
				setSupportError(
					ErrorCodes.API_INTERNAL_ERROR,
					'Attack Surface Page / Surface Data Request Error',
					error?.message || String(error)
				);
			} finally {
				if (active) setDataLoading(false);
			}
		})();
		return () => {
			active = false;
		};
	}, [selectedWatcher, days, dispatch, setSupportError, clearSupportError]);

	const filteredRows = React.useMemo(() => {
		const key = searchKey.trim().toLowerCase();
		if (!key) return rows;
		return rows.filter((row) => row.some((cell) => String(cell).toLowerCase().includes(key)));
	}, [rows, searchKey]);

	const { pageRows, pageNumber, totalRows, pageLimit, handlePageChange } = usePagination(filteredRows);

	const showErrorView = hasError && rows.length === 0;

	return (
		<Content
			currentMenu='externalbaseline'
			headerTitle='External Baseline Comms'
			searchBox={true}
			searchPlaceholder='Search external hosts'
			searchValue={searchKey}
			searchOnChange={(e) => setSearchKey(e.target.value)}
		>
			<div className={styles.intro}>
				A baseline of the external systems each internal device communicates with,
				as the Watchers observe it on the wire — who talks to what, over which
				ports, and how often.
			</div>

			{summary && (
				<div className={styles.summary_grid}>
					<div className={styles.summary_card}>
						<span className={styles.summary_value}>{summary.externalHosts}</span>
						<span className={styles.summary_label}>External Hosts</span>
					</div>
					<div className={styles.summary_card}>
						<span className={styles.summary_value}>{summary.internalDevices}</span>
						<span className={styles.summary_label}>Internal Devices</span>
					</div>
					<div className={styles.summary_card}>
						<span className={styles.summary_value}>{summary.countries}</span>
						<span className={styles.summary_label}>Countries</span>
					</div>
					<div className={styles.summary_card}>
						<span className={styles.summary_value}>{summary.totalConnections}</span>
						<span className={styles.summary_label}>Connections</span>
					</div>
				</div>
			)}

			<div className={styles.header_controls}>
				<div className={styles.control}>
					<SelectBox label='Watcher' value={selectedWatcher} onChange={(e) => setSelectedWatcher(e.target.value)}>
						<option value=''>Select a Watcher</option>
						<option value='All Watchers'>All Watchers</option>
						{watchers.map((w) => (w.length >= 2 ? (
							<option value={w[0]} key={w[0]}>{w[1]}</option>
						) : null))}
					</SelectBox>
				</div>
				<div className={styles.control}>
					<SelectBox label='Window' value={days} onChange={(e) => setDays(e.target.value)}>
						{WINDOWS.map((w) => (
							<option value={w[0]} key={w[0]}>{w[1]}</option>
						))}
					</SelectBox>
				</div>
			</div>

			{dataLoading ? (
				<Loader />
			) : showErrorView ? (
				<div>
					<NetworkError />
					{supportErrorMessage && (
						<div className={styles.supportErrorCard}>
							<strong>Support Error:</strong>
							<code className={styles.supportErrorCode}>{supportErrorMessage}</code>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button type='button' className={styles.copySupportErrorButton} onClick={handleCopySupportError}>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button type='button' className={styles.copySupportErrorButton} onClick={() => clearSupportError(true)}>
									Clear
								</button>
							</div>
						</div>
					)}
				</div>
			) : (
				<div className={styles.table_container}>
					<Table
						containerStyle={{ height: '100%', flex: '1 1 auto', overflow: 'auto' }}
						tableHead={
							<tr>
								{columns.map((col, index) => <th key={col + index}>{col}</th>)}
							</tr>
						}
						tableRow={
							pageRows.length > 0 ? (
								pageRows.map((row, rowIndex) => (
									<tr key={`surface-row-${rowIndex}`}>
										{row.map((cell, cellIndex) => (
											<td key={`surface-cell-${rowIndex}-${cellIndex}`}>{cell}</td>
										))}
									</tr>
								))
							) : (
								<tr>
									<td colSpan={columns.length || 8} style={{ textAlign: 'center' }}>
										No external connections observed for the selected watcher and window.
									</td>
								</tr>
							)
						}
					/>
					<div className='paginationContainer'>
						<Pagination
							onPageChange={handlePageChange}
							rowsPerPage={pageLimit}
							totalPageSize={totalRows}
							pageNumber={pageNumber}
						/>
					</div>
				</div>
			)}
		</Content>
	);
};

export default AttackSurfaceScreen;
